import { Fragment, FunctionComponent, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ConfigContext } from '../../configuration/ConfigContext';
import SimpleCheckBox from '../../forms/SimpleCheckBox';
import { usePropertyAssociationContext } from '../../propertyAssociation/PropertyAssociationContext';
import { updatePropertyAssociationSettings } from '../../settings/settingsService';
import { MembersSortDirection, MembersSortOrder } from '../memberConstants';
import { sortMembers } from '../membersSort';
import MemberDetailsModal from '../modals/MemberDetailsModal';
import { GetMembersResponse, Member } from '../models/Member';
import MemberItem from './MemberItem';

type Props = {
    defaultSortDirection: MembersSortDirection;
    defaultSortOrder: MembersSortOrder;
    isPending: boolean;
    membersResponse: GetMembersResponse | undefined;
    isLoading: boolean;
    error: any;
    refetch: () => void;
};

const MemberList: FunctionComponent<Props> = ({ defaultSortDirection, defaultSortOrder, isPending, membersResponse, refetch }) => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId } = usePropertyAssociationContext();
    const navigate = useNavigate();
    const location = useLocation();
    const { search } = useLocation();
    const [selectedMember, setSelectedMember] = useState<Member | undefined>();
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);
    const [sortDirection, setSortDirection] = useState(defaultSortDirection);
    const [sortedMembers, setSortedMembers] = useState<Member[]>([]);

    const emptyFunction = useCallback(() => {
        /* empty */
    }, []);

    useEffect(() => {
        const membersSorted = sortMembers(membersResponse?.members || [], sortOrder);
        if (sortDirection === MembersSortDirection.Descending) {
            membersSorted.reverse();
        }
        setSortedMembers(membersSorted);
    }, [membersResponse, sortOrder, sortDirection]);

    useEffect(() => {
        if (!search || !membersResponse?.members) {
            return;
        }

        const userId = new URLSearchParams(search).get('userId');
        const member = membersResponse.members.find((x) => x.user?.userId === userId);
        if (member) {
            setSelectedMember(member);
        }
    }, [search, membersResponse?.members]);

    const deselectMember = () => {
        navigate({ pathname: location.pathname }, { replace: true });
        setSelectedMember(undefined);
    };

    const showMemberDetails = (member: Member) => {
        const params = new URLSearchParams({ userId: member.user?.userId || '' });
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
        setSelectedMember(member);
    };

    const sortItems = (sortBy: MembersSortOrder) => {
        if (sortBy !== sortOrder) {
            setSortDirection(MembersSortDirection.Ascending);
            setSortOrder(sortBy);
            return;
        }

        if (sortDirection === MembersSortDirection.Ascending) {
            setSortDirection(MembersSortDirection.Descending);
            return;
        }

        setSortDirection(MembersSortDirection.Ascending);
    };

    const renderHeader = (text: string, sortBy: MembersSortOrder) => {
        if (sortBy !== sortOrder) {
            return (
                <th className="bookings__header bookings__header--no-sort" onClick={() => sortItems(sortBy)}>
                    {text}
                </th>
            );
        }

        if (sortDirection === MembersSortDirection.Ascending) {
            return (
                <th className="bookings__header bookings__header--ascending" onClick={() => sortItems(sortBy)}>
                    {text}
                </th>
            );
        }

        return (
            <th className="bookings__header bookings__header--descending" onClick={() => sortItems(sortBy)}>
                {text}
            </th>
        );
    };

    const toggleRequireMembership = () => {
        const requireMembership = !(membersResponse?.requireMembership ?? false);
        updatePropertyAssociationSettings(apiBaseUrl, currentPropertyAssociationId, { requireMembership }, refetch, emptyFunction);
    };

    return (
        <Fragment>
            <SimpleCheckBox
                name="requireMembership"
                label="Kräv manuellt hanterad åtkomst av folkbokförda användare för att dessa ska kunna boka resurser och ta del av anslagstavlan."
                isChecked={membersResponse?.requireMembership ?? false}
                onChange={toggleRequireMembership}
            />
            <table className="bookings">
                <thead className="bookings__head">
                    <tr>
                        {renderHeader('Förnamn', MembersSortOrder.FirstName)}
                        {renderHeader('Efternamn', MembersSortOrder.LastName)}
                        <th></th>
                        <th className="d-none d-md-table-cell"></th>
                    </tr>
                </thead>
                <tbody>
                    {sortedMembers.map((x) => (
                        <MemberItem key={x.user.userId} member={x} onClick={() => showMemberDetails(x)} requireMembership={membersResponse?.requireMembership ?? false} isPending={isPending} />
                    ))}
                </tbody>
            </table>
            {selectedMember && <MemberDetailsModal onClose={deselectMember} member={selectedMember} onMemberUpdated={refetch} isPending={isPending} />}
        </Fragment>
    );
};

export default MemberList;
